import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { queryKeys, STREET_VIEW_AUTO_FLAG, flagEnabled } from '@vtak/shared';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { usePluginStore } from '@/stores/plugin-store';
import { workspaceClient } from '@/services/workspace-client';
import { CORE_NAV_ITEMS } from '@/constants/navigation';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** A small on/off pill used for personal toggles. */
function Pill({ label, on, onToggle }: { label: string; on: boolean; onToggle: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'rounded-full border px-2.5 py-1 text-[11px] transition-colors',
        on
          ? 'border-echo-yellow/50 bg-echo-yellow/15 text-echo-yellow'
          : 'border-echo-border text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

/**
 * "My view" — personal dashboard tailoring. Each member can hide plugins and
 * core panels for themselves; the server overlays these onto their manifest so
 * every user's dashboard is unique to what they set. Unit config is untouched.
 */
export function SettingsMyView(): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const unitId = useWorkspaceStore((s) => s.workspace?.unitId ?? null);
  const mergedFlags = useWorkspaceStore((s) => s.workspace?.corePanelFlags);
  const activePlugins = usePluginStore((s) => s.activePlugins);

  const saved = useQuery({
    queryKey: ['workspace', 'my-view', unitId],
    enabled: unitId !== null,
    queryFn: () => workspaceClient.getMyView(unitId ?? ''),
  });

  const [hiddenPlugins, setHiddenPlugins] = useState<string[]>([]);
  const [panelFlags, setPanelFlags] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (saved.data) {
      setHiddenPlugins(saved.data.hiddenPlugins);
      setPanelFlags(saved.data.panelFlags);
    }
  }, [saved.data]);

  if (!unitId) return null;

  async function save(): Promise<void> {
    if (!unitId) return;
    setSaving(true);
    try {
      await workspaceClient.saveMyView(unitId, { hiddenPlugins, panelFlags });
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.config(unitId) });
      setFlash(true);
      setTimeout(() => setFlash(false), 2000);
    } finally {
      setSaving(false);
    }
  }

  const pluginOptions = activePlugins.filter((plugin) => plugin.id !== 'reference');

  return (
    <section>
      <p className="mb-1.5 px-2 font-chakra text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        My view
      </p>
      <p className="mb-2 px-2 text-[11px] leading-relaxed text-muted-foreground/80">
        Tailor your own dashboard — hidden items apply only to you.
      </p>
      <div className="space-y-2 px-2">
        <div className="flex flex-wrap gap-1">
          {pluginOptions.map((plugin) => (
            <Pill
              key={plugin.id}
              label={plugin.name}
              on={!hiddenPlugins.includes(plugin.id)}
              onToggle={() =>
                setHiddenPlugins((prev) =>
                  prev.includes(plugin.id) ? prev.filter((id) => id !== plugin.id) : [...prev, plugin.id],
                )
              }
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-1">
          {CORE_NAV_ITEMS.filter((item) => !item.hidden && item.id !== 'admin' && item.id !== 'settings').map(
            (item) => (
              <Pill
                key={item.id}
                label={item.label}
                on={panelFlags[item.id] !== false}
                onToggle={() => setPanelFlags((prev) => ({ ...prev, [item.id]: prev[item.id] === false }))}
              />
            ),
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {(() => {
            // Show the EFFECTIVE state (unit setting merged with my override) so a
            // unit-level disable reads as off, and one tap writes the explicit
            // override that re-enables it just for me.
            const effective = panelFlags[STREET_VIEW_AUTO_FLAG] ?? flagEnabled(mergedFlags, STREET_VIEW_AUTO_FLAG);
            return (
              <Pill
                label="Street View auto-dive"
                on={effective}
                onToggle={() =>
                  setPanelFlags((prev) => ({ ...prev, [STREET_VIEW_AUTO_FLAG]: !effective }))
                }
              />
            );
          })()}
        </div>
        <Button size="sm" className="w-full gap-1.5" disabled={saving} onClick={save}>
          {flash ? <Check className="h-3.5 w-3.5" /> : null}
          {saving ? 'Saving…' : flash ? 'Saved' : 'Save my view'}
        </Button>
      </div>
    </section>
  );
}
